/**
 * DPA (Direct Product Add) URL Builder Utility
 *
 * Centralizes the construction of DPA URLs used throughout the cart and checkout flow.
 */

const MAGENTO_URL = process.env.NEXT_PUBLIC_MAGENTO_URL;

export interface DpaUrlOptions {
  purchaseSource?: string;
  code?: string;
  reactivation?: string;
  useDpaKey?: boolean;
}

/**
 * Build a full DPA URL for adding products to cart
 * @param skus - Single SKU string or array of SKUs
 * @param options - Optional parameters (purchaseSource, code, reactivation)
 * @returns Full DPA URL
 *
 * @example
 * buildDpaUrl("ADV1-S01", { purchaseSource: "cart" })
 * // => "https://nutrafol.com/dpa/add/tocart?skus=ADV1-S01&purchase_source=cart"
 *
 * @example
 * buildDpaUrl(["ADV1-S01", "HAIRSERUM-N01"], { purchaseSource: "pdp", code: "SAVE20" })
 * // => "https://nutrafol.com/dpa/add/tocart?skus=ADV1-S01,HAIRSERUM-N01&purchase_source=pdp&code=SAVE20"
 */
export function buildDpaUrl(skus: string | string[], options: DpaUrlOptions = {}): string {
  const { purchaseSource = "cart", code, reactivation, useDpaKey = false } = options;

  const useDpaKeyBoolean = useDpaKey || skus?.includes("-SP");
  let skuString = Array.isArray(skus) ? skus.join(",") : skus;
  skuString = skuString.replaceAll("-SP", "-S");

  const params = new URLSearchParams();
  params.set("skus", skuString);

  if (purchaseSource) {
    params.set("purchase_source", purchaseSource);
  }

  if (code) {
    params.set("code", code);
  }

  if (reactivation) {
    params.set("reactivation", reactivation);
  }

  if (useDpaKeyBoolean) {
    params.set("promo_product_enabled", "true");
  }

  return `${MAGENTO_URL}dpa/add/tocart?${params.toString()}`;
}

/**
 * Build a relative DPA path (without the base URL)
 * Used for storing undo links in storage
 * @param skus - Single SKU string or array of SKUs
 * @param options - Optional parameters
 * @returns Relative DPA path starting with /
 *
 * @example
 * buildDpaPath("ADV1-S01")
 * // => "/dpa/add/tocart?skus=ADV1-S01"
 */
export function buildDpaPath(skus: string | string[], options: DpaUrlOptions = {}): string {
  const { purchaseSource, code, useDpaKey } = options;
  const useDpaKeyBoolean = useDpaKey || skus?.includes("-SP");
  let skuString = Array.isArray(skus) ? skus.join(",") : skus;
  skuString = skuString.replaceAll("-SP", "-S");

  const params = new URLSearchParams();
  params.set("skus", skuString);

  if (purchaseSource) {
    params.set("purchase_source", purchaseSource);
  }

  if (code) {
    params.set("code", code);
  }

  if (useDpaKeyBoolean) {
    params.set("promo_product_enabled", "true");
  }

  return `/dpa/add/tocart?${params.toString()}`;
}
